import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import "../../../assets/css/gamesCss/List/insertionSingly.css";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const LinkedListInsertionGame = () => {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [nodes, setNodes] = useState([]);
    const [headId, setHeadId] = useState(null);
    const [pendingNodes, setPendingNodes] = useState([]);
    const [lives, setLives] = useState(3);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [actionMode, setActionMode] = useState(null);
    const [reconnectFrom, setReconnectFrom] = useState(null);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [insertNodeMode, setInsertNodeMode] = useState(false);
    const [selectedPrevNode, setSelectedPrevNode] = useState(null);
    const [selectedNextNode, setSelectedNextNode] = useState(null);
    const [nodeToInsert, setNodeToInsert] = useState(null);
    const [visualHeadId, setVisualHeadId] = useState(null);
    const { darkMode } = useDarkMode();
    const { selLanguage } = useLanguage();

    const LEVELS = [
        {
            id: 1,
            title: selLanguage
                ? "Επίπεδο 1: Εισαγωγή στην Αρχή"
                : "Level 1: Insert at Beginning",
            description: selLanguage
                ? "Εισήγαγε νέο κόμβο με τιμή 5 στην αρχή της λίστας. Για την προσθήκη στην αρχή, κάνε διπλό κλικ στον κόμβο με το HEAD! Για την εισαγωγή ανάμεσα από δύο κόμβου πρέπει να επιλέξειςς αρχικά τον κόμβο που θες να εισάγεις και μετά να διαλέξεις τους 2 κόβμους που θες να τοποθετήσεις ανάμεσά τους."
                : "Insert a new node with value 5 at the beggining of the list. To add at the beginning, double-click the node with HEAD! To insert between two nodes, first select the node you want to insert and then choose the 2 nodes you want to place it between.",
            initialList: [
                { id: "B", value: "15", next: "C" },
                { id: "C", value: "25", next: "D" },
                { id: "D", value: "35", next: null },
            ],
            headId: "B",
            newNode: { id: "A", value: "5" },
            expectedHead: "A",
            expectedList: [
                { id: "A", value: "5", next: "B" },
                { id: "B", value: "15", next: "C" },
                { id: "C", value: "25", next: "D" },
                { id: "D", value: "35", next: null },
            ],
        },
        {
            id: 2,
            title: selLanguage
                ? "Επίπεδο 2: Εισαγωγή στο Τέλος"
                : "Level 2: Insert at End",
            description: selLanguage
                ? "Εισήγαγε νέο κόμβο με τιμή 100 στο τέλος της λίστας. "
                : "Insert a new node with value 100 at the end of the list.",
            initialList: [
                { id: "P", value: "10", next: "Q" },
                { id: "Q", value: "20", next: "R" },
                { id: "R", value: "30", next: null },
            ],
            headId: "P",
            newNode: { id: "S", value: "100" },
            expectedHead: "P",
            expectedList: [
                { id: "P", value: "10", next: "Q" },
                { id: "Q", value: "20", next: "R" },
                { id: "R", value: "30", next: "S" },
                { id: "S", value: "100", next: null },
            ],
        },
        {
            id: 3,
            title: selLanguage
                ? "Επίπεδο 3: Εισαγωγή Μετά από Συγκεκριμένο Κόμβο"
                : "Level 3: Insert After Specific Node",
            description: selLanguage
                ? "Εισήγαγε νέο κόμβο με τιμή 77 μετά τον κόμβο X."
                : "Insert a new node with value 77 after node X.",
            initialList: [
                { id: "W", value: "50", next: "X" },
                { id: "X", value: "60", next: "Y" },
                { id: "Y", value: "80", next: "Z" },
                { id: "Z", value: "90", next: null },
            ],
            headId: "W",
            newNode: { id: "M", value: "77" },
            insertAfter: "X",
            expectedHead: "W",
            expectedList: [
                { id: "W", value: "50", next: "X" },
                { id: "X", value: "60", next: "M" },
                { id: "M", value: "77", next: "Y" },
                { id: "Y", value: "80", next: "Z" },
                { id: "Z", value: "90", next: null },
            ],
        },
        {
            id: 4,
            title: selLanguage
                ? "Επίπεδο 4: Διπλή Εισαγωγή - Ultimate Challenge!"
                : "Level 4: Double Insertion – Ultimate Challenge!",
            description: selLanguage
                ? " Εισήγαγε δύο νέους κόμβους! Πρώτα τον E μετά τον B, και μετά τον G  στο τέλος της λίστας."
                : "Insert two new nodes! First insert E after B, then insert G at the end of the list.",
            initialList: [
                { id: "A", value: "10", next: "B" },
                { id: "B", value: "20", next: "C" },
                { id: "C", value: "30", next: "D" },
                { id: "D", value: "40", next: null },
            ],
            headId: "A",
            newNodes: [
                { id: "E", value: "22", insertAfter: "B" },
                { id: "G", value: "55", insertAtEnd: true },
            ],
            expectedHead: "A",
            expectedList: [
                { id: "A", value: "10", next: "B" },
                { id: "B", value: "20", next: "E" },
                { id: "E", value: "22", next: "C" },
                { id: "C", value: "30", next: "D" },
                { id: "D", value: "40", next: "G" },
                { id: "G", value: "55", next: null },
            ],
        },
    ];

    useEffect(() => {
        loadLevel(currentLevel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLevel]);

    const loadLevel = (levelIndex) => {
        const level = LEVELS[levelIndex];

        // Καθαρισμός τυχόν custom flags
        const initialNodes = level.initialList.map((n) => ({
            ...n,
            disconnected: false,
        }));

        setNodes(JSON.parse(JSON.stringify(initialNodes)));
        setHeadId(level.headId);
        setVisualHeadId(level.headId);


        if (level.newNodes) {
            setPendingNodes(level.newNodes.map((n) => ({ ...n, next: null })));
        } else {
            setPendingNodes([{ ...level.newNode, next: null }]);
        }

        setShowSuccess(false);
        setShowError(false);
        setErrorMessage("");
        setActionMode(null);
        setReconnectFrom(null);
        setShowGameOver(false);
        setInsertNodeMode(false);
        setSelectedPrevNode(null);
        setSelectedNextNode(null);
        setNodeToInsert(null);
        setShowGameOver(false);
    };

    const resetLevel = () => {
        loadLevel(currentLevel);
    };

    const handleMoveHead = (nodeId) => {
        setVisualHeadId(nodeId);
        setActionMode(null);
    };


    const baseButtonStyle = {
        padding: "16px 24px",
        borderRadius: "12px",
        border: "2px solid #7a8eff",
        fontSize: "1rem",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.2s ease",
        fontFamily: "Poppins, sans-serif",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
    };

    const getButtonStyle = (isActive) => ({
        ...baseButtonStyle,
        background: isActive ? "#7a8eff" : "transparent",
        color: isActive ? "#ffffff" : "#7a8eff",
        boxShadow: isActive
            ? "0 4px 12px rgba(122, 142, 255, 0.45)"
            : "0 4px 12px rgba(0, 0, 0, 0.15)",
    });


    // FIX 2: reconnect με χρήση prevNodes & καθάρισμα disconnected
    const handleReconnect = (fromId, toId) => {
        setNodes((prevNodes) =>
            prevNodes.map((node) =>
                node.id === fromId
                    ? { ...node, next: toId, disconnected: false }
                    : node
            )
        );
        setReconnectFrom(null);
        setActionMode(null);
    };

    const handleInsertNode = (pendingNode) => {
        if (!insertNodeMode) {
            setNodeToInsert(pendingNode);
            setInsertNodeMode(true);
            setSelectedPrevNode(null);
            setSelectedNextNode(null);
        } else if (nodeToInsert?.id === pendingNode.id) {
            // Αν ξαναπατήσεις τον ίδιο, ακυρώνει
            setInsertNodeMode(false);
            setNodeToInsert(null);
            setSelectedPrevNode(null);
            setSelectedNextNode(null);
        } else {
            setNodeToInsert(pendingNode);
            setSelectedPrevNode(null);
            setSelectedNextNode(null);
        }
    };

    const getOrderedNodes = () => {
        if (!headId || nodes.length === 0) return [];

        const ordered = [];
        let currentId = headId;
        const visited = new Set();

        while (currentId && !visited.has(currentId)) {
            const node = nodes.find((n) => n.id === currentId);
            if (!node) break;

            ordered.push(node);
            visited.add(currentId);
            currentId = node.next;
        }

        nodes.forEach((node) => {
            if (!visited.has(node.id)) {
                ordered.push(node);
            }
        });

        return ordered;
    };

    const orderedNodes = getOrderedNodes();

    const insertNodeAtBeginning = (newNodeData) => {
        if (!headId) return;

        const newNode = {
            id: newNodeData.id,
            value: newNodeData.value,
            next: headId
        };

        setNodes(prevNodes => [...prevNodes, newNode]);
        setPendingNodes(prev => prev.filter(n => n.id !== newNodeData.id));

        // ΛΟΓΙΚΑ η HEAD αλλάζει στον νέο κόμβο
        setHeadId(newNode.id);

        // ΔΕΝ ακουμπάμε το visualHeadId εδώ!
        setInsertNodeMode(false);
        setSelectedPrevNode(null);
        setSelectedNextNode(null);
        setNodeToInsert(null);
    };



    const insertNodeBetween = (prevNodeId, nextNodeId, newNodeData) => {
        // Αν το prev είναι null/undefined, δεν κάνουμε τίποτα (safety)
        if (!prevNodeId) return;

        const prevNode = orderedNodes.find((n) => n.id === prevNodeId);
        if (!prevNode) return;

        const expectedNext = prevNode.next === null ? "NULL" : prevNode.next;

        // Αν ο χρήστης δεν επιλέξει το πραγματικό "next", ακυρώνουμε
        if (nextNodeId !== expectedNext) {
            setSelectedPrevNode(null);
            setSelectedNextNode(null);
            setNodeToInsert(null);
            setInsertNodeMode(false);
            return;
        }

        const newNode = {
            id: newNodeData.id,
            value: newNodeData.value,
            next: nextNodeId === "NULL" ? null : nextNodeId,
            disconnected: false,
        };

        setNodes((prevNodes) => {
            const updatedNodes = prevNodes.map((n) =>
                n.id === prevNodeId ? { ...n, next: newNode.id, disconnected: false } : n
            );
            return [...updatedNodes, newNode];
        });

        setPendingNodes((prev) => prev.filter((n) => n.id !== newNodeData.id));

        setInsertNodeMode(false);
        setSelectedPrevNode(null);
        setSelectedNextNode(null);
        setNodeToInsert(null);
    };



    const checkSolution = () => {
        const level = LEVELS[currentLevel];
        let newLives = lives;

        if (pendingNodes.length > 0) {
            newLives = lives - 1;
            setErrorMessage(
                selLanguage
                    ? `Λάθος! Πρέπει να εισάγεις όλους τους κόμβους που περιμένουν!`
                    : `Wrong! You must insert all pending nodes!`
            );
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setTimeout(() => setShowGameOver(true), 1000);
            } else {
                setTimeout(() => setShowError(false), 2000);
            }
            return;
        }

        if (visualHeadId !== level.expectedHead) {
            newLives = lives - 1;
            setErrorMessage(
                selLanguage
                    ? `Λάθος! Προσπάθησε ξανά!`
                    : `Wrong! Try again!`
            );
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setTimeout(() => setShowGameOver(true), 1000);
            } else {
                setTimeout(() => setShowError(false), 2000);
            }
            return;
        }

        const currentIds = orderedNodes.map((n) => n.id).sort();
        const expectedIds = level.expectedList.map((n) => n.id).sort();

        if (JSON.stringify(currentIds) !== JSON.stringify(expectedIds)) {
            newLives = lives - 1;
            setErrorMessage(
                selLanguage
                    ? "Λάθος! Οι κόμβοι στη λίστα δεν είναι σωστοί"
                    : "Wrong! The nodes in the list are not correct"
            );
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setTimeout(() => setShowGameOver(true), 1000);
            } else {
                setTimeout(() => setShowError(false), 2000);
            }
            return;
        }

        for (const node of orderedNodes) {
            const expected = level.expectedList.find((n) => n.id === node.id);
            if (node.next !== expected.next) {
                newLives = lives - 1;
                setErrorMessage(
                    selLanguage
                        ? `Λάθος! Η σύνδεση του κόμβου ${node.id} δεν είναι σωστή`
                        : `Wrong! The connection of node ${node.id} is not correct`
                );
                setShowError(true);
                setLives(newLives);

                if (newLives === 0) {
                    setTimeout(() => setShowGameOver(true), 1000);
                } else {
                    setTimeout(() => setShowError(false), 2000);
                }
                return;
            }
        }

        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            if (currentLevel < LEVELS.length - 1) {
                setCurrentLevel((prev) => prev + 1);
                //setLives(3);
            } else {
                setShowGameWon(true);
            }
        }, 2500);
    };

    const handleNodeClick = (nodeId, isPending = false) => {
        if (actionMode === "move-head" && !isPending) {
            handleMoveHead(nodeId);
        } else if (actionMode === "disconnect") {
            setNodes((prevNodes) =>
                prevNodes.map((n) =>
                    n.id === nodeId ? { ...n, next: null, disconnected: true } : n
                )
            );
            setActionMode(null);
        } else if (actionMode === "reconnect" && !isPending) {
            if (!reconnectFrom) {
                setReconnectFrom(nodeId);
            } else if (reconnectFrom !== nodeId) {
                handleReconnect(reconnectFrom, nodeId === "NULL" ? null : nodeId);
            }
        } else if (insertNodeMode && !isPending) {
            if (!selectedPrevNode) {
                setSelectedPrevNode(nodeId);
            } else if (!selectedNextNode && nodeId !== selectedPrevNode) {
                setSelectedNextNode(nodeId);
                // Now we have both nodes, proceed with insertion
                if (nodeToInsert) {
                    insertNodeBetween(selectedPrevNode, nodeId, nodeToInsert);
                }
            }
        }
    };

    const getNodePosition = (index) => {
        const startX = 80;
        const spacing = 140;
        return { x: startX + index * spacing, y: 200 };
    };

    const level = LEVELS[currentLevel];

    return (
        <div
            className="main-content insertSinglyContainer"
            style={{
                background: darkMode
                    ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)"
                    : "var(--lightModeGameBackground)",
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={() => {
                    loadLevel(0);
                    setLives(3);
                    setShowGameOver(false);
                    setCurrentLevel(0)
                }}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={() => {
                    loadLevel(0);
                    setLives(3);
                    setShowGameWon(false);
                    setCurrentLevel(0);
                }}
            />

            {/* Header */}
            <div className="insertSinglyHeader">
                <div
                    style={{
                        color: darkMode ? "white" : "#1e293b",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode
                            ? "2px solid rgba(255, 255, 255, 0.2)"
                            : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    {selLanguage ? "Επίπεδο" : "Level"} {level.id}/{LEVELS.length}
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "right",
                        gap: "12px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center",
                        }}
                    >
                        {[1, 2, 3].map((heartNum) => (
                            <div
                                key={heartNum}
                                style={{
                                    fontSize: "24px",
                                    filter:
                                        lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                    transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                ❤️
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="insertSinglyProm">
                <p
                    style={{
                        color: "#ffffffff",
                        marginBottom: "15px",
                        lineHeight: "1.6",
                    }}
                >
                    {level.description}
                </p>
            </div>

            {/* Pending Nodes to Insert */}
            {pendingNodes.length > 0 && (
                <div
                    style={{
                        background: "#dbeafe",
                        borderRadius: "15px",
                        padding: "20px",
                        marginBottom: "20px",
                        border: "2px solid #3b82f6",
                        width: "90%",
                        maxWidth: "770px",
                    }}
                >
                    <h3 style={{ color: "#1e40af", marginBottom: "15px" }}>
                        {" "}
                        {selLanguage ? "Κόμβοι προς Εισαγωγή" : "Nodes to Insert"}
                    </h3>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {pendingNodes.map((node, index) => (
                            <motion.div
                                key={node.id}
                                style={{
                                    background:
                                        nodeToInsert?.id === node.id ? "#10b981" : "#60a5fa",
                                    color: "white",
                                    padding: "15px 25px",
                                    borderRadius: "12px",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    cursor: "pointer",
                                    border:
                                        nodeToInsert?.id === node.id
                                            ? "3px solid #065f46"
                                            : "3px solid white",
                                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                                }}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: index * 0.1 }}
                                onClick={() => handleInsertNode(node)}
                                whileHover={{ scale: 1.05 }}
                            >
                                {node.id} = {node.value}
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div
                style={{
                    display: "flex",
                    gap: "34px",
                    justifyContent: "center",
                }}
            >
                <button
                    onClick={() =>
                        setActionMode(actionMode === "move-head" ? null : "move-head")
                    }
                    style={getButtonStyle(actionMode === "move-head")}
                    onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                >
                    {selLanguage ? "Μετακίνηση HEAD" : "Move HEAD"}
                </button>
                {/*<button
                    onClick={() => {
                        setActionMode(actionMode === "disconnect" ? null : "disconnect");
                    }}
                    style={getButtonStyle(actionMode === "disconnect")}                >
                    {selLanguage ? "Αφαίρεση Σύνδεσης" : "Remove connection"}
                </button>*/}

                {/*<button
                    onClick={() => {
                        setActionMode(actionMode === "reconnect" ? null : "reconnect");
                        setReconnectFrom(null);
                    }}
                    style={getButtonStyle(actionMode === "reconnect")}
                    onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                >
                    {selLanguage ? "Σύνδεση Κόμβων" : "Connect nodes"}
                </button>*/}

                {/*{pendingNodes.length > 0 && (
                    <button
                        onClick={() => {
                            if (insertNodeMode) {
                                setInsertNodeMode(false);
                                setSelectedPrevNode(null);
                                setSelectedNextNode(null);
                                setNodeToInsert(null);
                            } else if (pendingNodes.length > 0) {
                                setInsertNodeMode(true);
                            }
                        }}
                        style={{
                            ...getButtonStyle(actionMode === "insert-node"),
                            background: insertNodeMode ? "#120d30a9" : "none",
                        }}
                        onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                        onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                    >
                        {insertNodeMode
                            ? selLanguage
                                ? "Ακύρωση Εισαγωγής"
                                : "Cancel insertion"
                            : selLanguage
                                ? "Προσθήκη Κόμβου"
                                : "Add node"}
                    </button>
                )}*/}
            </div>

            {reconnectFrom && (
                <div
                    style={{
                        background: "#fed7aa",
                        padding: "12px",
                        borderRadius: "8px",
                        textAlign: "center",
                        marginBottom: "20px",
                        border: "2px solid #f59e0b",
                    }}
                >
                    {selLanguage
                        ? `Επέλεξε τον κόμβο στον οποίο θέλεις να συνδέσεις το ${reconnectFrom}`
                        : `Select the node you want to connect ${reconnectFrom} to`}
                </div>
            )}

            {/* Linked List Visualization */}
            <div className="insertSinglyVisual">
                <svg width="100%" height="400" viewBox="0 0 1000 400">
                    <defs>
                        <marker
                            id="arrowhead"
                            markerWidth="10"
                            markerHeight="10"
                            refX="9"
                            refY="3"
                            orient="auto"
                        >
                            <polygon points="0 0, 10 3, 0 6" fill="#3b82f6" />
                        </marker>
                    </defs>

                    {/* Draw connections */}
                    {orderedNodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        // Αν η ακμή έχει διαγραφεί (disconnected), δεν δείχνουμε τίποτα
                        if (node.disconnected) {
                            return null;
                        }

                        if (node.next) {
                            const nextNode = orderedNodes.find((n) => n.id === node.next);
                            if (nextNode) {
                                const nextIndex = orderedNodes.indexOf(nextNode);
                                const nextPos = getNodePosition(nextIndex);

                                return (
                                    <motion.line
                                        key={`edge-${node.id}`}
                                        x1={pos.x + 60}
                                        y1={pos.y}
                                        x2={nextPos.x - 10}
                                        y2={nextPos.y}
                                        stroke="#3b82f6"
                                        strokeWidth="3"
                                        markerEnd="url(#arrowhead)"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                );
                            }
                        } else {
                            // ΜΟΝΟ για κανονικά tails (next === null και ΔΕΝ έχει γίνει disconnect)
                            const nullPos = getNodePosition(orderedNodes.length);
                            return (
                                <motion.line
                                    key={`edge-${node.id}-null`}
                                    x1={pos.x + 60}
                                    y1={pos.y}
                                    x2={nullPos.x}
                                    y2={nullPos.y}
                                    stroke="#3b82f6"
                                    strokeWidth="3"
                                    markerEnd="url(#arrowhead)"
                                    strokeDasharray="5,5"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.5 }}
                                />
                            );
                        }
                        return null;
                    })}

                    {/* Draw HEAD indicator */}
                    {visualHeadId && orderedNodes.find((n) => n.id === visualHeadId) && (
                        <motion.g initial={{ y: -20, opacity: 0 }} animate={{ y: -15, opacity: 1 }}>
                            <text
                                x={
                                    getNodePosition(
                                        orderedNodes.findIndex((n) => n.id === visualHeadId)
                                    ).x + 30
                                }
                                y={
                                    getNodePosition(
                                        orderedNodes.findIndex((n) => n.id === visualHeadId)
                                    ).y - 40
                                }
                                textAnchor="middle"
                                fill="#10b981"
                                fontSize="20"
                                fontWeight="700"
                            >
                                HEAD
                            </text>
                            <line
                                x1={
                                    getNodePosition(
                                        orderedNodes.findIndex((n) => n.id === visualHeadId)
                                    ).x + 30
                                }
                                y1={
                                    getNodePosition(
                                        orderedNodes.findIndex((n) => n.id === visualHeadId)
                                    ).y - 30
                                }
                                x2={
                                    getNodePosition(
                                        orderedNodes.findIndex((n) => n.id === visualHeadId)
                                    ).x + 30
                                }
                                y2={
                                    getNodePosition(
                                        orderedNodes.findIndex((n) => n.id === visualHeadId)
                                    ).y - 10
                                }
                                stroke="#10b981"
                                strokeWidth="3"
                                markerEnd="url(#arrowhead)"
                            />
                        </motion.g>
                    )}

                    {/* Draw nodes */}
                    {orderedNodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        return (
                            <motion.g
                                key={node.id}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <rect
                                    x={pos.x}
                                    y={pos.y - 25}
                                    width="60"
                                    height="50"
                                    fill={
                                        (insertNodeMode && selectedPrevNode === node.id) ? '#a7f3d0' :
                                            (insertNodeMode && nodeToInsert) ? '#fef3c7' :
                                                '#eff6ff'
                                    }
                                    stroke={
                                        (insertNodeMode && selectedPrevNode === node.id) ? '#10b981' :
                                            '#3b82f6'
                                    }
                                    strokeWidth="3"
                                    rx="8"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleNodeClick(node.id, false)}
                                    onDoubleClick={() => {
                                        // μόνο όταν είμαστε σε insert mode, έχουμε επιλεγμένο pending node
                                        // και ο κόμβος που πατήσαμε είναι ο HEAD
                                        if (insertNodeMode && nodeToInsert && node.id === visualHeadId) {
                                            insertNodeAtBeginning(nodeToInsert);
                                        }
                                    }}
                                />

                                <text
                                    x={pos.x + 30}
                                    y={pos.y - 5}
                                    textAnchor="middle"
                                    fill="#1e293b"
                                    fontSize="14"
                                    fontWeight="700"
                                    style={{ pointerEvents: "none" }}
                                >
                                    {node.id}
                                </text>
                                <text
                                    x={pos.x + 30}
                                    y={pos.y + 12}
                                    textAnchor="middle"
                                    fill="#64748b"
                                    fontSize="12"
                                    style={{ pointerEvents: "none" }}
                                >
                                    {node.value}
                                </text>

                                <circle cx={pos.x + 55} cy={pos.y} r="5" fill="#3b82f6" />
                            </motion.g>
                        );
                    })}

                    {/* NULL box */}
                    {orderedNodes.length > 0 && (
                        <g>
                            <rect
                                x={getNodePosition(orderedNodes.length).x}
                                y={getNodePosition(orderedNodes.length).y - 15}
                                width="50"
                                height="30"
                                fill={
                                    insertNodeMode && nodeToInsert && selectedPrevNode
                                        ? "#fef3c7"
                                        : "#1f2937"
                                }
                                stroke={
                                    insertNodeMode && nodeToInsert && selectedPrevNode
                                        ? "#f59e0b"
                                        : "#64748b"
                                }
                                strokeWidth="2"
                                rx="5"
                                style={{
                                    cursor:
                                        (actionMode === "reconnect" && reconnectFrom) ||
                                            (insertNodeMode && selectedPrevNode)
                                            ? "pointer"
                                            : "default",
                                }}
                                onClick={() => {
                                    if (actionMode === "reconnect" && reconnectFrom) {
                                        handleNodeClick("NULL", false);
                                    } else if (insertNodeMode && selectedPrevNode && nodeToInsert) {
                                        setSelectedNextNode("NULL");
                                        insertNodeBetween(selectedPrevNode, "NULL", nodeToInsert);
                                    }
                                }}
                            />
                            <text
                                x={getNodePosition(orderedNodes.length).x + 25}
                                y={getNodePosition(orderedNodes.length).y + 5}
                                textAnchor="middle"
                                fill="#94a3b8"
                                fontSize="14"
                                fontWeight="700"
                                style={{ pointerEvents: "none" }}
                            >
                                NULL
                            </text>
                        </g>
                    )}
                </svg>
            </div>

            {/* Success/Error Messages */}
            <AnimatePresence>
                {showSuccess && (
                    <motion.div
                        style={{
                            background: '#d1fae5',
                            borderRadius: '15px',
                            padding: '20px',
                            marginBottom: '20px',
                            border: '2px solid #10b981',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        initial={{ scale: 0, y: 50 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0, y: -50 }}
                    >
                        {selLanguage ? "Μπράβο! Σωστή λύση!" : "Correct solution!"}
                    </motion.div>
                )}

                {showError && (
                    <motion.div
                        style={{
                            background: '#fee2e2',
                            borderRadius: '15px',
                            padding: '20px',
                            marginBottom: '20px',
                            border: '2px solid #dc2626',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        initial={{ scale: 0, y: 50 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0, y: -50 }}
                    >
                        {errorMessage}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Control Buttons */}
            <div style={{ display: "flex", gap: "15px", justifyContent: "center" }}>
                <button
                    onClick={checkSolution}
                    className="singlyInsertionCheckButton"
                    onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                >
                    {selLanguage ? "Έλεγχος Λύσης" : "Check solution"}
                </button>
                <button
                    onClick={resetLevel}
                    className="singlyInsertionResetButton"
                    onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                >
                    {selLanguage ? "Επαναφορά" : "Restart"}
                </button>
            </div>
        </div>
    );
};

export default LinkedListInsertionGame;
